import { useEffect } from 'react';

import { getLinkedCollectionElements } from 'editor/src/store/design/util/collectionLink';
import { useSelector } from 'editor/src/store/hooks';
import { AdvancedPersonalisationElementData } from 'editor/src/store/plugins/utils/advPersoUtils';

import sendPostMessage from 'editor/src/util/postMessages/sendPostMessage';

const requested = new Set<string>();

// Sync-on-select resolves sibling assets from state.collections.folders; a folder that
// never rendered (hidden linked secondary) would otherwise never load and the sync
// would silently skip it. Prefetch every folder of the link group up front.
function useLinkedCollectionFolders(collectionInfo: AdvancedPersonalisationElementData) {
  const siblingFolderIds = useSelector((state) => {
    if (!collectionInfo.linkGroupId || !state.design.designData) {
      return undefined;
    }
    const ids = getLinkedCollectionElements(state.design.designData, collectionInfo.linkGroupId)
      .map((linked) => linked.collectionInfo.folderId)
      .filter((folderId) => !state.collections.folders[folderId]);
    return ids.length ? ids.join(',') : undefined;
  });

  // Folders that have already arrived but are still marked in-flight — clear them from
  // `requested` so a dropped postMessage for a *different* folder doesn't get stuck forever;
  // mirrors useCollectionFolder's per-folder cleanup.
  const arrivedRequestedFolderIds = useSelector((state) => {
    if (!collectionInfo.linkGroupId || !state.design.designData) {
      return undefined;
    }
    const ids = getLinkedCollectionElements(state.design.designData, collectionInfo.linkGroupId)
      .map((linked) => linked.collectionInfo.folderId)
      .filter((folderId) => state.collections.folders[folderId] && requested.has(folderId));
    return ids.length ? ids.join(',') : undefined;
  });

  useEffect(() => {
    siblingFolderIds?.split(',').forEach((folderId) => {
      if (!requested.has(folderId)) {
        requested.add(folderId);
        sendPostMessage('advancedPersonalisation.getFolderContent', folderId);
      }
    });
  }, [siblingFolderIds]);

  useEffect(() => {
    arrivedRequestedFolderIds?.split(',').forEach((folderId) => {
      requested.delete(folderId);
    });
  }, [arrivedRequestedFolderIds]);
}

export default useLinkedCollectionFolders;
